import { usePapersCount } from "./usePapersCount";
import { useCycleSignals, type CycleSignalsResponse } from "./useCycleSignals";
import { useConcept } from "./useConcepts";
import { useCurrentUser, isPaidPlan } from "./useCurrentUser";
import { useFieldDebate } from "./useFieldDebate";
import { useWritingDraft } from "./useWritingDraft";
import { useScoringStore } from "@/stores/scoring-store";
import { useBackgroundScoringPending } from "./useScoringBatchStatus";
import { EMPTY_SNAPSHOT, type ConceptSnapshot } from "@/lib/workflowAdvisor";
import { hasResearchQuestion } from "@/lib/conceptGuidance";
import type { PaperCountResponse } from "@/types/paper";

export interface SnapshotParts {
  counts: PaperCountResponse;
  cycle: CycleSignalsResponse;
  concept: {
    abstract: string;
    abstract_auto_drafted?: boolean;
    argument_analysis: unknown;
    last_definition_edit_at: string | null;
  };
  hasFieldDebate: boolean;
  campSurfaced: boolean;
  isPaid: boolean;
  isScoring: boolean;
  atScoringLimit: boolean;
  hasTopPaper: boolean;
  topPaperOpened: boolean;
  reviewedCount: number;
  unscoredCount: number;
  papersOutOfBoundary: number;
  conceptEdited: boolean;
  hasDraft: boolean;
  untriagedCount: number;
}

/** Pure mapping — unit-tested. */
export function toSnapshot(p: SnapshotParts): ConceptSnapshot {
  return {
    ...EMPTY_SNAPSHOT,
    hasDescription: hasResearchQuestion(p.concept.abstract),
    draftedQuestion: !!p.concept.abstract_auto_drafted,
    seedCount: p.counts.foundational,
    foundCount: p.counts.trawled,
    scoredCount: p.counts.scored,
    // Papers already in a scoring run aren't a to-do: "Score N papers" while
    // a background batch is scoring them reads as a conflicting instruction.
    unscoredCount: p.isScoring ? 0 : p.unscoredCount,
    papersOutOfBoundary: p.papersOutOfBoundary,
    conceptEdited: p.conceptEdited,
    atScoringLimit: p.atScoringLimit,
    hasTopPaper: p.hasTopPaper,
    topPaperOpened: p.topPaperOpened,
    reviewedCount: p.reviewedCount,
    hasFieldDebate: p.hasFieldDebate,
    campSurfaced: p.campSurfaced,
    auditEnacted: p.cycle.audit_enacted,
    drillEnacted: p.cycle.drill_enacted,
    hasChatMessages: p.cycle.has_chat_messages,
    hasHighlights: p.cycle.has_highlights,
    hasClaims: p.cycle.has_claims,
    hasClaimsWithEvidence: p.cycle.has_claims_with_evidence,
    hasSmartTrawlPapers: p.cycle.has_smart_trawl_papers,
    hasArgumentAnalysis: p.concept.argument_analysis != null,
    hasDraft: p.hasDraft,
    untriagedCount: p.untriagedCount,
    isPaid: p.isPaid,
  };
}

/**
 * Globally-derivable snapshot for the map panel and proactive card.
 * reviewedCount, unscoredCount, papersOutOfBoundary, topPaperOpened, hasDraft,
 * atScoringLimit are only fully known on Curate; this global hook defaults them
 * (0/false) and the Curate page passes an enriched snapshot where it has them.
 */
export function useConceptSnapshot(conceptId: string | undefined): ConceptSnapshot | null {
  const { data: counts } = usePapersCount(conceptId);
  const { data: cycle } = useCycleSignals(conceptId);
  const { data: concept } = useConcept(conceptId);
  const { data: user } = useCurrentUser();
  const { data: debateMap } = useFieldDebate(conceptId ?? "");
  const inlineScoring = useScoringStore((s) => s.isScoring);
  const isScoring = useBackgroundScoringPending(conceptId) || inlineScoring;
  const isPaid = isPaidPlan(user?.plan);
  const { draft } = useWritingDraft(conceptId ?? "", {
    enabled: !!conceptId && isPaid,
  });

  if (!conceptId || !counts || !cycle || !concept) return null;

  return toSnapshot({
    counts,
    cycle,
    concept: {
      abstract: concept.abstract ?? "",
      abstract_auto_drafted: concept.abstract_auto_drafted,
      argument_analysis: concept.argument_analysis ?? null,
      last_definition_edit_at: concept.last_definition_edit_at ?? null,
    },
    hasFieldDebate: !!debateMap,
    campSurfaced: (debateMap?.camps?.length ?? 0) > 0,
    isPaid,
    isScoring,
    atScoringLimit: false,
    hasTopPaper: counts.scored > 0,
    topPaperOpened: false,
    reviewedCount: counts.reviewed ?? 0,
    unscoredCount: counts.unscored ?? 0,
    papersOutOfBoundary: 0,
    conceptEdited: false,
    hasDraft: !!draft?.content,
    untriagedCount: counts.untriaged ?? 0,
  });
}
